import { Caption1, Input, makeStyles, tokens } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { PersonAvatar } from "./PersonAvatar";
import { MockCaseMenu } from "./MockCaseMenu";
import { inviteLoginHref, useSession } from "./session";
import {
  PUBLIC_INVITE_ALREADY,
  PUBLIC_INVITE_BLOCKED,
  PUBLIC_INVITE_CLOSED,
  PUBLIC_INVITE_NOTE,
  PUBLIC_INVITE_SENT,
  PUBLIC_INVITE_WORKSPACE,
  inviteIssueSentence,
  publicInviteState,
  rejectRequestImpact,
  requestPublicAccess,
  type PublicAccessResult,
} from "./mock-plan-access";

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
  },
  stage: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  bar: {
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: `0 ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    maxWidth: "420px",
    width: "100%",
    margin: "0 auto",
    boxSizing: "border-box",
  },
  identity: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  name: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightRegular,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  note: { color: tokens.colorNeutralForeground3 },
  bad: { color: tokens.colorPaletteRedForeground1 },
});

function resultSentence(result: PublicAccessResult): string {
  if (result.ok) return PUBLIC_INVITE_SENT;
  if (result.reason === "차단") return PUBLIC_INVITE_BLOCKED;
  if (result.reason === "대기") return rejectRequestImpact();
  if (result.reason === "이미") return PUBLIC_INVITE_ALREADY;
  return inviteIssueSentence("형식");
}

/** 공용 초대 링크. 이름과 아이콘만 보이고, 요청은 멤버가 되지 않는다. */
export function InviteLinkPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const session = useSession();
  const { token } = useParams();
  const [forcedClosed, setForcedClosed] = useState(false);
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<PublicAccessResult | null>(null);
  const open = !forcedClosed && publicInviteState(token ?? "") === "open";

  useEffect(() => {
    if (session) setEmail(session.email);
  }, [session]);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  if (!open) {
    return (
      <div className={styles.page}>
        <header className={styles.bar}>
          <MockCaseMenu
            cases={[
              { id: "open", label: "열림" },
              { id: "closed", label: "열 수 없음" },
            ]}
            onPick={(id) => {
              setForcedClosed(id === "closed");
              setResult(null);
            }}
          />
        </header>
        <div className={styles.stage}>
          <GhostEmptyState title={PUBLIC_INVITE_CLOSED} />
        </div>
      </div>
    );
  }

  const sentence = result ? resultSentence(result) : "";

  return (
    <div className={styles.page}>
      <header className={styles.bar}>
        <MockCaseMenu
          cases={[
            { id: "open", label: "열림" },
            { id: "closed", label: "열 수 없음" },
          ]}
          onPick={(id) => {
            setForcedClosed(id === "closed");
            setResult(null);
          }}
        />
      </header>
      <main className={styles.main}>
        <div className={styles.identity}>
          <PersonAvatar name={PUBLIC_INVITE_WORKSPACE} size={32} />
          <div className={styles.name} title={PUBLIC_INVITE_WORKSPACE}>{PUBLIC_INVITE_WORKSPACE}</div>
        </div>
        <Caption1 className={styles.note}>{PUBLIC_INVITE_NOTE}</Caption1>
        {result ? (
          <Caption1 className={result.ok ? styles.note : styles.bad}>{sentence}</Caption1>
        ) : !session ? (
          <>
            <Caption1 className={styles.note}>로그인한 뒤에 접근을 요청합니다.</Caption1>
            <div>
              <FuiButton appearance="primary" density="dialog" onClick={() => navigate(inviteLoginHref(`/invite/t/${token ?? ""}`))}>
                로그인
              </FuiButton>
            </div>
          </>
        ) : (
          <>
            <Input
              size="small"
              value={email}
              placeholder="이메일"
              aria-label="이메일"
              onChange={(_, data) => setEmail(data.value)}
            />
            <div>
              <FuiButton
                appearance="primary"
                density="dialog"
                disabled={email.trim() === ""}
                onClick={() => setResult(requestPublicAccess(email))}
              >
                접근 요청
              </FuiButton>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
